// De app op het beginscherm zetten.
//
// Waarom dit meer is dan een extraatje: op een iPhone geeft Apple alleen
// meldingen aan apps die op het beginscherm staan. Wie de app in Safari laat
// staan, krijgt dus nooit bericht dat zijn team zo moet rijden - en merkt dat
// niet, want er gaat gewoon niets af. Datzelfde staat op `/meldingen`, maar wie
// daar nooit komt leest het nooit.
//
// Elk toestel doet het anders, en dat verschil is niet cosmetisch:
//
//   Android         de browser geeft ons een echte installatieknop
//   iOS met Safari  geen knop, alleen aanwijzingen - Apple heeft er geen API voor
//   iOS anders      kan het helemaal niet; alleen Safari mag dit op iOS
//
// Daarom vier gevallen en niet twee. Een knop tonen die niets doet is erger dan
// geen knop.

/** Draait deze pagina als geinstalleerde app op het beginscherm? */
export function alsApp(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // Safari op iOS gebruikt zijn eigen vlag.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // Een iPad meldt zich sinds iPadOS 13 als Macintosh; het aanraakscherm
  // verraadt hem.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export type Toestel = "android" | "ios-safari" | "ios-anders" | "bureaublad";

export function leesToestel(): Toestel {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;

  if (isIOS()) {
    // Op iOS draait élke browser op WebKit en meldt zich als Safari. Chrome,
    // Edge en Firefox zetten er hun eigen woord bij, en alleen zij kunnen dit
    // niet - de knop "Zet op beginscherm" zit in Safari.
    return /CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/.test(ua) ? "ios-anders" : "ios-safari";
  }
  if (/Android/.test(ua)) return "android";
  return "bureaublad";
}

// Onthouden wat iemand ervan vond. Twee sleutels, want "nu even niet" en
// "hij staat er al" zijn verschillende dingen: het eerste mag terugkomen, het
// tweede nooit meer.
const LATER_SLEUTEL = "ntto-installeren-later";
const KLAAR_SLEUTEL = "ntto-installeren-klaar";

/** Hoe lang we zwijgen na een "Later". Twee weken: lang genoeg om niet te
 *  zeuren, kort genoeg om voor de volgende wedstrijd terug te zijn. */
const LATER_DAGEN = 14;

function lees(sleutel: string): string | null {
  try {
    return window.localStorage.getItem(sleutel);
  } catch {
    // Privemodus of geblokkeerde opslag. Dan liever niets tonen dan bij elke
    // paginawissel opnieuw beginnen.
    return null;
  }
}

function schrijf(sleutel: string, waarde: string): void {
  try {
    window.localStorage.setItem(sleutel, waarde);
  } catch {
    // Zie hierboven; niet kunnen onthouden is geen reden om te breken.
  }
}

/** Mag de tip nu getoond worden? */
export function magTonen(): boolean {
  if (typeof window === "undefined") return false;
  if (alsApp()) return false;
  if (leesToestel() === "bureaublad") return false;
  if (lees(KLAAR_SLEUTEL)) return false;

  const later = lees(LATER_SLEUTEL);
  if (later) {
    const tot = Number(later) + LATER_DAGEN * 24 * 60 * 60 * 1000;
    if (Number.isFinite(tot) && Date.now() < tot) return false;
  }
  return true;
}

export function onthoudLater(): void {
  schrijf(LATER_SLEUTEL, String(Date.now()));
}

export function onthoudKlaar(): void {
  schrijf(KLAAR_SLEUTEL, "1");
}

// Het gebeurtenistype dat Chrome geeft. Staat niet in de standaard-typen van
// TypeScript, want het is geen webstandaard maar iets van Chromium.
export type InstallatiePrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

// Chrome vuurt `beforeinstallprompt` af zodra de pagina laadt - vaak voordat
// React iets doet. Een klein script in de layout vangt hem daarom op en legt
// hem hier neer; wij komen hem later ophalen. Zonder dat vangnet is de knop er
// wel, maar doet hij niets.
declare global {
  interface Window {
    __nttoInstallatie?: InstallatiePrompt | null;
  }
}

export function haalPrompt(): InstallatiePrompt | null {
  if (typeof window === "undefined") return null;
  return window.__nttoInstallatie ?? null;
}

export function vergeetPrompt(): void {
  if (typeof window !== "undefined") window.__nttoInstallatie = null;
}

/** Naam van de gebeurtenis waarmee "Meer" de tip opnieuw kan openen. */
export const OPEN_GEBEURTENIS = "ntto:installeren";
